/* © NXAPP Limited */
import { Fragment, type ReactNode } from 'react'

/** Inline: **bold**, `code`, [text](url). */
function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g
  let last = 0, m: RegExpExecArray | null, i = 0
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    if (m[2] !== undefined) out.push(<strong key={`${key}-b${i}`} className="font-semibold text-gray-900">{m[2]}</strong>)
    else if (m[3] !== undefined) out.push(<code key={`${key}-c${i}`} className="px-1 py-0.5 rounded bg-gray-100 text-[0.85em] text-gray-800 font-mono">{m[3]}</code>)
    else if (m[4] !== undefined) out.push(<a key={`${key}-a${i}`} href={m[5]} target="_blank" rel="noreferrer" className="text-brand-600 underline">{m[4]}</a>)
    last = m.index + m[0].length; i++
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Minimal, line-based markdown → React. Headings, lists, code fences, quotes, paragraphs. */
export default function Markdown({ source }: { source: string }) {
  const lines = source.replace(/\r/g, '').split('\n')
  const blocks: ReactNode[] = []
  let i = 0, k = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim() === '') { i++; continue }

    // Code fence
    if (line.startsWith('```')) {
      const buf: string[] = []; i++
      while (i < lines.length && !lines[i].startsWith('```')) { buf.push(lines[i]); i++ }
      i++
      blocks.push(<pre key={k++} className="rounded-lg bg-gray-900 text-gray-100 text-xs p-3 overflow-x-auto my-3"><code>{buf.join('\n')}</code></pre>)
      continue
    }

    // Headings
    const h = line.match(/^(#{1,3})\s+(.*)$/)
    if (h) {
      const lvl = h[1].length
      const cls = lvl === 1 ? 'text-xl font-bold text-gray-900 mt-6 mb-2'
        : lvl === 2 ? 'text-base font-semibold text-gray-900 mt-5 mb-1.5'
        : 'text-sm font-semibold text-gray-700 mt-4 mb-1'
      blocks.push(<div key={k++} className={cls}>{inline(h[2], `h${k}`)}</div>)
      i++; continue
    }

    // Blockquote / tip
    if (line.startsWith('> ')) {
      const buf: string[] = []
      while (i < lines.length && lines[i].startsWith('> ')) { buf.push(lines[i].slice(2)); i++ }
      blocks.push(<div key={k++} className="border-l-2 border-brand-300 bg-brand-50/40 pl-3 py-1.5 my-3 text-sm text-gray-700">{inline(buf.join(' '), `q${k}`)}</div>)
      continue
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s+/, '')); i++ }
      blocks.push(<ol key={k++} className="list-decimal pl-5 my-2 space-y-1 text-sm text-gray-700">{items.map((it, j) => <li key={j}>{inline(it, `ol${k}-${j}`)}</li>)}</ol>)
      continue
    }

    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^[-*]\s+/, '')); i++ }
      blocks.push(<ul key={k++} className="list-disc pl-5 my-2 space-y-1 text-sm text-gray-700">{items.map((it, j) => <li key={j}>{inline(it, `ul${k}-${j}`)}</li>)}</ul>)
      continue
    }

    // Paragraph (gather until blank line)
    const para: string[] = []
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,3}\s|[-*]\s|\d+\.\s|>\s|```)/.test(lines[i])) { para.push(lines[i]); i++ }
    blocks.push(<p key={k++} className="text-sm text-gray-700 leading-relaxed my-2">{inline(para.join(' '), `p${k}`)}</p>)
  }

  return <Fragment>{blocks}</Fragment>
}
